import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { d as debounce_1 } from './index4.js';
import { c as sortData, d as SortIcon, e as SortOrder, S as StringService, B as ButtonIcons, f as switchOrder, M as MultipleAlternatives } from './index2.js';
import { D as DomService, T as TrackingService } from './index3.js';
import { T as TranslationService } from './index6.js';
import { b as ButtonType, d as defineCustomElement$5 } from './roche-button2.js';
import { I as InputType, d as defineCustomElement$3 } from './roche-input2.js';
import { r as remCalc, b as breakpoints } from './index5.js';
import { d as defineCustomElement$4 } from './roche-icon2.js';
import { d as defineCustomElement$2 } from './roche-tooltip2.js';

const rocheResponsiveTableCss = ":host{display:block;margin-top:3rem;margin-top:0}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}@media (min-width: 64rem){:host{margin-top:0}}:host .table{min-height:12.5rem;}:host .table>div{font-family:\"roche-sans\";table-layout:fixed;width:100%;margin-top:1.5rem;overflow-x:auto;display:block}@media (min-width: 768px){:host .table>div{display:table}}:host .table__header{background:transparent;position:relative;display:table-row}:host .table__header.cloned{position:fixed;top:0;background:#fff}:host .table__headline,:host .table__item{padding:1rem;display:table-cell}:host .table__headline.clonedMobile,:host .table__item.clonedMobile{display:none}:host .table__item{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:var(--body-small--font-size--small-up);line-height:1.625rem;border-bottom:1px solid rgba(0, 0, 0, 0.2);vertical-align:top;color:#312f2e}@media (min-width: 48rem){:host .table__item{font-size:var(--body-small--font-size--medium-up);line-height:1.8125rem}}:host .table__item.single-column{background-color:#f5f5f2;border-bottom:1px solid #9d948e}:host .table__headline{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-weight:400;font-size:var(--button--font-size--small-up);line-height:1.3125rem;border-bottom:2px solid var(--color-interactive--primary-idle);vertical-align:bottom}@media (min-width: 48rem){:host .table__headline{font-size:var(--button--font-size--medium-up);line-height:1.4375rem}}:host .table__items{font-family:\"roche-sans\";font-style:normal;font-weight:200;color:var(--color-text--primary);font-size:var(--body--font-size--small-up);line-height:1.8125rem;display:table-row;width:100%}@media (min-width: 64rem){:host .table__items{font-size:var(--body--font-size--xlarge-up);line-height:2rem}}:host .table__items.odd-row .table__item,:host .table__items.single-row .table__item{background-color:#f5f5f2;border-bottom:1px solid #9d948e}:host .table__items.odd-column .table__item:nth-child(odd){background-color:#f5f5f2;border-bottom:1px solid #9d948e}:host .table__items:first-child{}:host .table__items:first-child .table__item{border-bottom:1px solid rgba(0, 0, 0, 0.2)}:host .table__items.hidden{display:none}:host .table__no-results{font-size:1rem;margin:1.5rem 0 0 0;display:none;align-items:center;position:absolute}:host .table__no-results--show{display:flex}:host .table__no-results roche-icon{margin:0 0.75rem 0 0;fill:var(--color-interactive--disabled)}:host .table--sortable .table__headline--sortable{position:relative;padding-right:2.25rem;cursor:pointer;}:host .table--sortable .table__headline--sortable:hover roche-icon{fill:var(--color-interactive--primary-hover)}:host .table--sortable .table__headline--sortable roche-icon{position:absolute;right:0.625rem;bottom:1.4375rem;fill:var(--color-interactive--primary-idle)}:host .table--sortable .table__headline--sortable roche-icon:hover{fill:var(--color-interactive--primary-hover)}:host .table--sticky-header.table--is-mobile .table__headline.clonedMobile{display:table-cell;position:absolute;background:#fff}:host .table--is-mobile{table-layout:auto;overflow-x:auto}:host .table--is-mobile .table__row{display:flex;}:host .table--is-mobile .table__row .table__item,:host .table--is-mobile .table__row .table__headline{min-width:10rem}:host .table--is-mobile .table__row .table__item{width:100%}:host .table--is-mobile .table__row .table__headline{border-top:none;border-right:2px solid var(--color-interactive--primary-idle);border-bottom:1px solid rgba(0, 0, 0, 0.2);width:10rem}:host .table--is-mobile .table__row:first-child .table__item{border-top:1px solid rgba(0, 0, 0, 0.2)}:host .table--is-mobile .table__row:first-child .table__headline{border-top:1px solid rgba(0, 0, 0, 0.2)}:host .filter{display:none}:host .filter--visible{display:block;display:flex;justify-content:flex-start;align-items:center}:host .filter roche-input{font-family:\"roche-sans\";font-style:normal;font-weight:200;font-size:var(--body-small--font-size--small-up);line-height:1.625rem;width:28.125rem;margin-right:1.5rem;margin-top:0}@media (min-width: 48rem){:host .filter roche-input{font-size:var(--body-small--font-size--medium-up);line-height:1.8125rem}}:host .filter roche-button{margin-top:0}:host(roche-responsive-table[align-text=right]) .table__headline,:host(roche-responsive-table[align-text=right]) .table__item{text-align:right}:host([dir=rtl]) .table__headline,:host([dir=rtl]) .table__item{text-align:left}:host([dir=rtl]) .filter roche-input{margin-left:1.5rem;margin-right:0}:host([dir=rtl]) .table--sortable .table__headline--sortable{padding-right:1rem;padding-left:2.25rem;}:host([dir=rtl]) .table--sortable .table__headline--sortable roche-icon{right:auto;left:0.625rem}";
const RocheResponsiveTableStyle0 = rocheResponsiveTableCss;

const MINIMUM_COLUMN_WIDTH = 160;
const CHEVRON_DOWN_ICON_SIZE_LTR = 12;
const CHEVRON_DOWN_ICON_SIZE_RTL = 16;
const RocheResponsiveTable$1 = /*@__PURE__*/ proxyCustomElement(class RocheResponsiveTable extends HTMLElement {
    get headersParsed() {
        if (this.headers instanceof Array) {
            return this.headers;
        }
        return this.headers ? JSON.parse(this.headers) : [];
    }
    get rowsParsed() {
        if (this.rows instanceof Array) {
            return this.rows;
        }
        return this.rows ? JSON.parse(this.rows) : [];
    }
    get hasHeaders() {
        return !!this.headersParsed.length;
    }
    get filterInput() {
        return this.element.shadowRoot.querySelector('.filter__input').shadowRoot.querySelector('input');
    }
    // eslint-disable-next-line class-methods-use-this
    get defaultTracking() {
        return {
            event: 'responsive_table_functions',
            component_name: 'roche-responsive-table',
        };
    }
    handleScroll() {
        if (this.stickyHeader && !this.isSmall && this.loaded) {
            this.handleStickyHeader();
            this.updateClonedWidth();
        }
    }
    updateHeaders() {
        this.updateSortBy();
    }
    updateData() {
        const { headersParsed, rowsParsed } = this;
        const minimumColumns = Math.max(headersParsed.length, rowsParsed.reduce((max, row) => Math.max(max, row.length), 0));
        const headers = [...new Array(minimumColumns)].map((_, index) => headersParsed[index] || '');
        const rows = [...new Array(rowsParsed.length)].map((_, rowIndex) => ([...new Array(minimumColumns)].map((__, index) => rowsParsed[rowIndex][index] || '')));
        this.largeViewportData = [headers, ...sortData(rows, this.sortBy, this.sortOrder)];
        this.smallViewportData = headers.map((header, index) => ([
            ...(headersParsed.length ? [header] : []),
            ...rows.map((row) => row[index]),
        ]));
        this.updateSize();
    }
    handleAccordionTabsState() {
        if (this.stickyHeader) {
            this.handleStickyHeaderSmall();
        }
    }
    updateSortIcon(index) {
        if (!this.sortOrder) {
            return SortIcon.DESCENDING;
        }
        return this.sortOrder === SortOrder.ASCENDING && this.sortBy === index
            ? SortIcon.ASCENDING
            : SortIcon.DESCENDING;
    }
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.headers = undefined;
        this.rows = undefined;
        this.stickyHeader = false;
        this.sortable = false;
        this.filterable = false;
        this.alignText = undefined;
        this.noResults = undefined;
        this.alternatedColumn = undefined;
        this.alternatedRow = undefined;
        this.multipleAlternatives = undefined;
        this.sortBy = undefined;
        this.sortOrder = undefined;
        this.loaded = false;
        this.largeViewportData = [];
        this.smallViewportData = [];
        this.isSmall = false;
        this.translations = undefined;
        this.handleKeyUp = debounce_1.debounce(this.handleKeyUp.bind(this), 250);
        this.handleResize = debounce_1.debounce(this.handleResize.bind(this), 250);
        this.updateSortIcon = this.updateSortIcon.bind(this);
    }
    async componentWillLoad() {
        this.translations = {
            clearFilter: await TranslationService.translate('clear filter'),
            filter: await TranslationService.translate('filter'),
        };
        this.updateData();
        DomService.createObserver(this.element, this.handleResize);
    }
    componentDidUpdate() {
        if (this.stickyHeader && !this.isSmall && this.loaded) {
            this.handleStickyHeader();
        }
        if (this.stickyHeader && this.isSmall) {
            this.handleStickyHeaderSmall();
        }
        this.handleAlternatives();
    }
    componentDidLoad() {
        if (this.stickyHeader && this.isSmall) {
            this.handleStickyHeaderSmall();
        }
        DomService.handleTextDirection(this.element);
        this.loaded = true;
    }
    render() {
        return (h(Host, { key: '90cb81b7cc5d5f05c6d71e1a40f55bc619738fa8', ref: (element) => { this.hostTable = element; } }, h("div", { key: '2bc02a05be980daa6d180317f9e628a036f0b357', class: { filter: true, 'filter--visible': this.filterable && !this.isSmall } }, h("roche-input", { key: '4783f22e0e33fbaac7a0fac99354479d87c0967e', class: "filter__input", name: "inputFilter", value: "", type: InputType.TEXT, placeholder: StringService.toTitleCase(this.translations.filter), onKeyUp: this.handleKeyUp }), h("roche-button", { key: '701427fe5fcb502e708c950e583015d2cec53566', text: StringService.capitalize(this.translations.clearFilter), type: ButtonType.BUTTON, "left-icon": ButtonIcons.DEFAULT, onRocheButtonClick: this.handleClearFilter.bind(this), disabled: true, ref: (element) => { this.clearFilter = element; } })), h("div", { key: 'd08667f35efda3df447543c497a7774bd2e039b3', class: {
                table: true,
                'table--is-mobile': this.isSmall,
                'table--sticky-header': this.stickyHeader,
                'table--sortable': this.sortable && !this.isSmall,
            } }, this.isSmall ? this.getSmallRender() : this.getLargeRender())));
    }
    getSmallRender() {
        return (h("div", null, this.smallViewportData.map((row, index) => (h("div", { key: index, class: "table__row" }, row.map((item, innerIndex) => (h("div", { key: `table-small-row-${innerIndex}`, class: innerIndex === 0 ? 'table__headline' : 'table__item', onClick: this.handleSort(index), innerHTML: item }))))))));
    }
    getLargeRender() {
        const [headers, ...rows] = this.largeViewportData;
        return (h("div", { ref: (element) => { this.innerTable = element; } }, this.hasHeaders && (h("div", { class: "table__header", ref: (element) => { this.tableHeader = element; } }, headers.map((tableHead, index) => (h("div", { key: `table-large-header-${index}`, class: { table__headline: true, 'table__headline--sortable': this.sortable }, onClick: this.handleSort(index) }, tableHead, this.sortable
            ? h("roche-icon", { "data-sort-icon": this.updateSortIcon(index), icon: this.updateSortIcon(index), height: `${remCalc(DomService.isRtl(this.element) ? CHEVRON_DOWN_ICON_SIZE_RTL : CHEVRON_DOWN_ICON_SIZE_LTR)}rem` }) : ''))))), rows.map((tableRow, index) => (h("div", { key: index, class: "table__items", ref: (element) => { this.tableItems = element; } }, tableRow.map((item, innerIndex) => (h("div", { key: `table-large-row-${innerIndex}`, class: "table__item", innerHTML: item })))))), h("div", { class: "table__no-results", ref: (element) => { this.noResultsElement = element; } }, h("roche-icon", { icon: 'mobile', height: `${remCalc(24)}rem` }), this.noResults || 'Sorry! Your filter request has no results. Please try another term.')));
    }
    updateSortBy(index) {
        this.sortOrder = switchOrder(index !== undefined && index === this.sortBy ? this.sortOrder : undefined);
        this.sortBy = index;
        TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { event: 'sort', value: this.sortOrder, additionalParameters: {
                sortedColumnNr: this.sortBy,
                sortedColumn: this.hasHeaders ? this.headersParsed[this.sortBy] : '',
            } }));
    }
    handleSort(clickedHeader) {
        if (!this.sortable) {
            return undefined;
        }
        return () => {
            this.updateSortBy(clickedHeader);
        };
    }
    updateSize() {
        const { width } = this.element.getBoundingClientRect();
        this.isSmall = width > breakpoints.large
            ? false
            : (width / this.largeViewportData[0].length) < MINIMUM_COLUMN_WIDTH;
    }
    handleResize() {
        this.updateSize();
        if (this.stickyHeader && !this.isSmall && this.loaded) {
            this.handleStickyHeader();
            this.updateClonedWidth();
        }
        if (this.stickyHeader && this.isSmall && this.loaded) {
            this.handleStickyHeaderSmall();
            this.updateClonedWidthMobile();
        }
    }
    handleClearFilter() {
        this.filterInput.value = '';
        this.handleKeyUp();
        TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { event: 'clear_filter' }));
    }
    handleKeyUp() {
        const { value } = this.filterInput;
        if (value.length > 0) {
            this.clearFilter.disabled = false;
            this.clearFilter.leftIcon = ButtonIcons.CLEAR;
        }
        else {
            this.clearFilter.disabled = true;
            this.clearFilter.leftIcon = ButtonIcons.DEFAULT;
        }
        const lowerCaseLargeViewportData = this.largeViewportData.map((element) => element.map((childElement) => childElement.toLowerCase()));
        const indexesToShow = lowerCaseLargeViewportData.map((element) => element.find((item) => item.includes(value.toLowerCase())));
        Array.from(this.innerTable.children).forEach((element, index) => {
            if (!element.classList.contains('table__no-results')) {
                indexesToShow.forEach((item, innerIndex) => {
                    if (item && index === innerIndex) {
                        element.classList.remove('hidden');
                    }
                    else if (!item && index === innerIndex) {
                        element.classList.add('hidden');
                    }
                });
            }
        });
        const results = Array.from(this.innerTable.children).filter((child) => child.classList.contains('hidden') === false);
        if (results.length === 1) {
            this.noResultsElement.classList.add('table__no-results--show');
        }
        else {
            this.noResultsElement.classList.remove('table__no-results--show');
        }
        if (value.length > 0) {
            TrackingService.push(Object.assign(Object.assign({}, this.defaultTracking), { event: 'filter', value: this.filterInput.value, additionalParameters: {
                    filteredResults: results.length - 1,
                } }));
        }
    }
    handleStickyHeader() {
        if (!this.tableHeader || !this.element || !this.tableItems) {
            return;
        }
        const tableShadowRoot = this.element.shadowRoot;
        const { top: headerTop } = this.tableHeader.getBoundingClientRect();
        const { bottom: tableBottom } = this.element.getBoundingClientRect();
        const clonedElementExist = tableShadowRoot.querySelector('.cloned') ? tableShadowRoot.querySelector('.cloned') : '';
        const tableItems = tableShadowRoot.querySelectorAll('.table__items');
        const { height: lastItemHeight } = tableItems[tableItems.length - 1].getBoundingClientRect();
        Array.from(tableShadowRoot.querySelectorAll('.clonedMobile')).forEach((element) => {
            element.remove();
        });
        if (headerTop <= 0 && tableBottom > lastItemHeight) {
            if (clonedElementExist && this.sortable) {
                this.updateIconOnCloneHeader();
            }
            if (!clonedElementExist) {
                const clonedHeader = this.tableHeader.cloneNode(true);
                const headlinesWidth = Array.from(this.tableHeader.children).map((element) => {
                    const { width } = element.getBoundingClientRect();
                    return width;
                });
                clonedHeader.classList.add('cloned');
                this.innerTable.appendChild(clonedHeader);
                if (this.sortable) {
                    this.updateIconOnCloneHeader();
                }
                Array.from(clonedHeader.children).forEach((element, index) => {
                    element.setAttribute('style', `width:${headlinesWidth[index]}px`);
                    element.addEventListener('click', this.handleSort(index));
                });
            }
        }
        else if (clonedElementExist) {
            tableShadowRoot.querySelector('.cloned').remove();
        }
    }
    handleStickyHeaderSmall() {
        const tableShadowRoot = this.element.shadowRoot;
        const headers = Array.from(tableShadowRoot.querySelectorAll('.table__headline'));
        const clonedHeaders = tableShadowRoot.querySelectorAll('.clonedMobile');
        const clonedFromDesktop = tableShadowRoot.querySelector('.cloned');
        const normalHeaders = headers.filter((header) => !header.classList.contains('clonedMobile'));
        if (clonedFromDesktop) {
            clonedFromDesktop.remove();
        }
        if (this.hasMultipleHeadersInSameRow(clonedHeaders)) {
            clonedHeaders.forEach((element) => element.remove());
        }
        else if (clonedHeaders.length === normalHeaders.length) {
            return;
        }
        headers.forEach((element) => {
            const { height, width } = element.getBoundingClientRect();
            if (width === 0 && height === 0) {
                return;
            }
            const clonedHeader = element.cloneNode(true);
            clonedHeader.classList.add('clonedMobile');
            element.parentElement.appendChild(clonedHeader);
            // +1 to avoid text passing behind the headline (border issues),
            // also removed 1px from padding within scss file
            // eslint-disable-next-line no-param-reassign
            clonedHeader.style.width = `${width + 1}px`;
            // eslint-disable-next-line no-param-reassign
            clonedHeader.style.height = `${height}px`;
        });
    }
    // eslint-disable-next-line class-methods-use-this
    hasMultipleHeadersInSameRow(clonedHeaders) {
        return clonedHeaders.length > 1 && clonedHeaders[0].parentElement.querySelectorAll('.clonedMobile').length > 1;
    }
    updateClonedWidth() {
        if (this.tableHeader) {
            const clonedElement = this.element.shadowRoot.querySelector('.cloned');
            const { width } = this.tableHeader.getBoundingClientRect();
            if (clonedElement) {
                Array.from(clonedElement.children).forEach((element) => {
                    element.setAttribute('style', `width:${width / clonedElement.children.length}px`);
                });
            }
        }
    }
    updateClonedWidthMobile() {
        const tableShadowRoot = this.element.shadowRoot;
        const headers = Array.from(tableShadowRoot.querySelectorAll('.table__headline')).filter((header) => !header.classList.contains('.clonedMobile'));
        const clonedElements = tableShadowRoot.querySelectorAll('.clonedMobile');
        if (!clonedElements) {
            return;
        }
        headers.forEach((element) => {
            const { height, width } = element.getBoundingClientRect();
            if (width === 0 && height === 0) {
                return;
            }
            const clonedHeader = element.parentElement.querySelector('.clonedMobile');
            if (!clonedHeader) {
                return;
            }
            // +1 to avoid text passing behind the headline (border issues),
            // also removed 1px from padding within scss file
            // eslint-disable-next-line no-param-reassign
            clonedHeader.style.width = `${width + 1}px`;
            // eslint-disable-next-line no-param-reassign
            clonedHeader.style.height = `${height}px`;
        });
    }
    updateIconOnCloneHeader() {
        const clonedElement = this.hostTable.shadowRoot.querySelector('.cloned');
        if (clonedElement) {
            // eslint changes to const automatically
            // eslint-disable-next-line prefer-const
            let iconList = [];
            Array.from(this.tableHeader.children).forEach((element) => {
                const rocheIcon = element.querySelector('roche-icon');
                iconList.push(rocheIcon.getAttribute('data-sort-icon'));
            });
            Array.from(clonedElement.querySelectorAll('roche-icon')).forEach((element, index) => {
                element.setAttribute('icon', iconList[index]);
                element.setAttribute('height', `${remCalc(12)}rem`);
            });
        }
    }
    handleAlternatives() {
        if (this.innerTable) {
            const { children } = this.innerTable;
            const lastChildren = this.innerTable.children.length;
            if (this.multipleAlternatives === MultipleAlternatives.ROWS) {
                Array.from(children).forEach((element, index) => {
                    if (index % 2 !== 0) {
                        element.classList.add('odd-row');
                    }
                });
            }
            if (this.multipleAlternatives === MultipleAlternatives.COLUMNS) {
                Array.from(children).forEach((element, index) => {
                    if (index !== 0 || index !== lastChildren) {
                        element.classList.add('odd-column');
                    }
                });
            }
            if (this.alternatedColumn && !this.multipleAlternatives) {
                Array.from(children).forEach((element, index) => {
                    if ((index !== 0 || index !== lastChildren)
                        && this.alternatedColumn <= element.children.length) {
                        element.children[this.alternatedColumn - 1].classList.add('single-column');
                    }
                });
            }
            if (this.alternatedRow && !this.multipleAlternatives) {
                Array.from(children).forEach((element, index) => {
                    if (index === this.alternatedRow
                        && this.alternatedRow <= element.parentElement.children.length) {
                        element.classList.add('single-row');
                    }
                });
            }
        }
    }
    get element() { return this; }
    static get watchers() { return {
        "headers": ["updateHeaders", "updateData"],
        "rows": ["updateData"],
        "sortBy": ["updateData", "updateSortIcon"],
        "sortOrder": ["updateData"]
    }; }
    static get style() { return RocheResponsiveTableStyle0; }
}, [1, "roche-responsive-table", {
        "headers": [1],
        "rows": [1],
        "stickyHeader": [4, "sticky-header"],
        "sortable": [4],
        "filterable": [4],
        "alignText": [1, "align-text"],
        "noResults": [1, "no-results"],
        "alternatedColumn": [2, "alternated-column"],
        "alternatedRow": [2, "alternated-row"],
        "multipleAlternatives": [1, "multiple-alternatives"],
        "sortBy": [32],
        "sortOrder": [32],
        "loaded": [32],
        "largeViewportData": [32],
        "smallViewportData": [32],
        "isSmall": [32],
        "translations": [32]
    }, [[9, "scroll", "handleScroll"], [0, "rocheAccordionTabsItemOpen", "handleAccordionTabsState"]], {
        "headers": ["updateHeaders", "updateData"],
        "rows": ["updateData"],
        "sortBy": ["updateData", "updateSortIcon"],
        "sortOrder": ["updateData"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-responsive-table", "roche-button", "roche-icon", "roche-input", "roche-tooltip"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-responsive-table":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RocheResponsiveTable$1);
            }
            break;
        case "roche-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "roche-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "roche-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const RocheResponsiveTable = RocheResponsiveTable$1;
const defineCustomElement = defineCustomElement$1;

export { RocheResponsiveTable, defineCustomElement };

//# sourceMappingURL=roche-responsive-table.js.map